import React, { useEffect, useState } from 'react';
import { PaymentElement, LinkAuthenticationElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import styles from '@utmb/features/bus-reservation/bus-reservation.module.scss';
import { Button } from '@utmb/design-system/button/Button';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { useUser } from '@utmb/contexts/AuthContext';
import { useTranslation } from 'next-i18next';
import { Font } from '@utmb/design-system/text/font';

export default function CheckoutForm() {
    const stripe = useStripe();
    const elements = useElements();
    const { me } = useUser();
    const [, setEmail] = useState('');
    const [message, setMessage] = useState<string | undefined>('');
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    let callbackUrl = '';
    if (typeof window !== 'undefined') {
        callbackUrl = window.location.href;
        callbackUrl = callbackUrl.split('?')[0];
    }

    useEffect(() => {
        setEmail(me?.email ? me.email : '');
    }, [me]);

    useEffect(() => {
        if (!stripe) {
            return;
        }

        const clientSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');

        if (!clientSecret) {
            return;
        }

        stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
            switch (paymentIntent?.status) {
                case 'succeeded':
                    setMessage(t('busReservation.paymentSucceed'));
                    break;
                case 'processing':
                    setMessage(t('busReservation.paymentProcessingMsg'));
                    break;
                case 'requires_payment_method':
                    setMessage(t('busReservation.paymentFailed'));
                    break;
                default:
                    setMessage(t('busReservation.paymentWrong'));
                    break;
            }
        });
    }, [stripe]);

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setIsLoading(true);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                // Make sure to change this to your payment completion page
                return_url: callbackUrl,
            },
        });

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Otherwise, your customer will be redirected to
        // your `return_url`. For some payment methods like iDEAL, your customer will
        // be redirected to an intermediate site first to authorize the payment, then
        // redirected to the `return_url`.
        if (error.type === 'card_error' || error.type === 'validation_error') {
            setMessage(error.message);
        } else {
            setMessage('An unexpected error occurred.');
        }

        setIsLoading(false);
    };

    const paymentElementOptions: StripePaymentElementOptions = {
        layout: 'tabs',
    };

    return (
        <form id="payment-form" onSubmit={handleSubmit} className={styles.paymentForm}>
            <LinkAuthenticationElement id="link-authentication-element" onChange={(e) => setEmail(e.value.email)} />
            <PaymentElement id="payment-element" options={paymentElementOptions} />
            <Button.Default
                color={getColorStyle(ColorTypesEnum.SECONDARY)}
                bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                type="submit"
                disabled={isLoading || !stripe || !elements}
                label={isLoading ? t('busReservation.paymentProcessing') : t('busReservation.payNow')}
                className={styles.button}
                id="submit"
            />
            {message ? (
                <Font.OxaniumMedium as="div" mobile="14" desktop="14" color={ColorTypesEnum.RED}>
                    {message}
                </Font.OxaniumMedium>
            ) : null}
        </form>
    );
}
